export type Point = { x: number; y: number };

export type IntroVisualState = {
  progress: number;
  ship: Point & { rotation: number };
  groundY: number;
  groundAlpha: number;
  mountainsY: number;
  mountainsAlpha: number;
  planetY: number;
  planetAlpha: number;
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

export const cubicBezier = (
  progressInput: number,
  p0: Point,
  p1: Point,
  p2: Point,
  p3: Point,
): Point => {
  const progress = clamp01(progressInput);
  const inverse = 1 - progress;
  return {
    x:
      inverse ** 3 * p0.x +
      3 * inverse ** 2 * progress * p1.x +
      3 * inverse * progress ** 2 * p2.x +
      progress ** 3 * p3.x,
    y:
      inverse ** 3 * p0.y +
      3 * inverse ** 2 * progress * p1.y +
      3 * inverse * progress ** 2 * p2.y +
      progress ** 3 * p3.y,
  };
};

export const introPosition = (progress: number, width: number, height: number): Point => {
  const state = introVisualState(progress, width, height);
  return { x: state.ship.x, y: state.ship.y };
};

export const clampProgress = clamp01;

const remap = (value: number, start: number, end: number): number =>
  clamp01((value - start) / (end - start));

const smooth = (value: number): number => {
  const progress = clamp01(value);
  return progress * progress * (3 - 2 * progress);
};

const lerp = (start: number, end: number, progress: number): number =>
  start + (end - start) * progress;

export const introVisualState = (
  rawProgress: number,
  width: number,
  height: number,
): IntroVisualState => {
  const progress = clamp01(rawProgress);
  const start = { x: width * 0.12, y: height * 0.78 };
  const liftoff = { x: width * 0.2, y: height * 0.59 };
  const ascent = { x: width * 0.285, y: height * 0.49 };
  const ready = { x: width * 0.3, y: height * 0.5 };

  let shipX = start.x;
  let shipY = start.y;
  let rotation = 0;

  if (progress < 0.12) {
    const ignition = remap(progress, 0, 0.12);
    shipX += Math.sin(ignition * Math.PI * 10) * width * 0.0015;
    shipY += Math.sin(ignition * Math.PI * 12) * height * 0.003;
    rotation = Math.sin(ignition * Math.PI * 8) * 0.018;
  } else if (progress < 0.38) {
    const phase = smooth(remap(progress, 0.12, 0.38));
    shipX = lerp(start.x, liftoff.x, phase);
    shipY = lerp(start.y, liftoff.y, phase);
    rotation = lerp(-0.12, -0.04, phase);
  } else if (progress < 0.68) {
    const phase = smooth(remap(progress, 0.38, 0.68));
    shipX = lerp(liftoff.x, ascent.x, phase);
    shipY = lerp(liftoff.y, ascent.y, phase);
    rotation = lerp(-0.04, 0.025, phase);
  } else if (progress < 0.88) {
    const phase = smooth(remap(progress, 0.68, 0.88));
    shipX = lerp(ascent.x, ready.x, phase);
    shipY = lerp(ascent.y, ready.y, phase);
    rotation = lerp(0.025, 0, phase);
  } else {
    shipX = ready.x;
    shipY = ready.y;
  }

  const groundExit = smooth(remap(progress, 0.2, 0.8));
  const mountainExit = smooth(remap(progress, 0.28, 0.82));

  return {
    progress,
    ship: { x: shipX, y: shipY, rotation },
    groundY: lerp(0, height * 0.42, groundExit),
    groundAlpha: 1 - smooth(remap(progress, 0.62, 0.84)),
    mountainsY: lerp(0, height * 0.62, mountainExit),
    mountainsAlpha: 1 - smooth(remap(progress, 0.54, 0.82)),
    planetY: height * 1.89 + height * 0.98 * smooth(remap(progress, 0.35, 0.86)),
    planetAlpha: 1 - smooth(remap(progress, 0.7, 0.9)),
  };
};
